import { yupResolver } from "@hookform/resolvers/yup";
import { useParams } from "react-router-dom";
import * as yup from "yup";

import { courseStudentsApi } from "api/courseStudentsApi";
import { useCourseTranchesOptions } from "api/hooks";
import { PaziaBox } from "components/atoms";
import { HeaderWithBreadcrumbs, PageMain } from "components/molecules";
import { routes } from "consts";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useToaster } from "utils";
import {
  PaziaButton,
  PaziaFormButton,
  PaziaLoader,
  PaziaSelect,
} from "components/atoms";
import { REGISTRATION_TRANCHE_PAYMENT_STATUSES_LABELS, RegistrationTrancheStatus } from "consts";
import { TanTableClient, TanTableColumn } from "components/molecules";
import { formatDate } from "utils";
import { CourseCreateProvider } from "../CourseCreate/CourseCreateContext";
import { useCourseQuery } from "../CourseCreate/useCourseQuery";
import { InviteUsersFromCsvModal } from "./InviteUsersFromCsvModal/InviteUsersFromCsvModal";

export const CourseInviteUsersPage = () => {
  const { courseId } = useParams();
  const [users, setUsers] = useState<InviteUserData[]>([]);
  const { showSuccess, showError } = useToaster();
  const methods = useForm<FormValues>({
    resolver: yupResolver(schema),
  });
  const { handleSubmit } = methods;
  const [isModalOpen, setModalOpen] = useState(false);
  const { isLoading: loadingTranches, options: tranches } = useCourseTranchesOptions(courseId);

  const { isLoading, course } = useCourseQuery(courseId);

  const handleImportUsers = async (importedUsers: InviteUserData[]) => {
    setUsers((prev) => [...prev, ...importedUsers]);
  };

  if (isLoading) return <PaziaLoader position="center" />;
  if (!course) return null;

  const onSubmit = async (data: FormValues) => {
    try {
      await courseStudentsApi.invite(Number(courseId), {
        advanceCourseTrancheId: data.advanceCourseTrancheId,
        restCourseTrancheId: data.restCourseTrancheId,
        users: users,
      });
      showSuccess("Użytkownicy zostali zaproszeni na kurs.");
    } catch (error) {
      console.error(error);
      showError("Wystąpił błąd podczas zapraszania użytkowników na kurs.");
    }
  };

  const columns: TanTableColumn<InviteUserData>[] = [
    { id: "idx", header: "Lp.", accessorKey: "idx" },
    {
      id: "email",
      accessorKey: "email",
      header: "Email",
      enableColumnFilter: false,
    },
    {
      id: "firstName",
      accessorKey: "firstName",
      header: "Imię",
      enableColumnFilter: false,
    },
    {
      id: "surname",
      accessorKey: "surname",
      header: "Nazwisko",
      enableColumnFilter: false,
    },
    { id: "bornPlace", header: "Miejsce urodzenia", accessorKey: "bornPlace" },
    { id: "address", header: "Adres", accessorKey: "address" },
    {
      id: "bornDateTime",
      header: "Data urodzenia",
      accessorKey: "bornDateTime",
      cell: (row) => formatDate(row.getValue<Date>()),
    },
    { id: "pesel", header: "PESEL", accessorKey: "pesel" },
    { id: "buyer", header: "Kupujący", accessorKey: "buyer" },
    { id: "phone", header: "Telefon", accessorKey: "phone" },
    { id: "buyerAddress", header: "Adres", accessorKey: "buyerAddress" },
    { id: "buyerNIP", header: "NIP", accessorKey: "buyerNIP" },
    { id: "advancePayment", header: "Zaliczka", accessorKey: "advancePayment" },
    {
      id: "advancePaymentStatus",
      header: "Zaliczka status",
      accessorKey: "advancePaymentStatus",
      cell: (row) => REGISTRATION_TRANCHE_PAYMENT_STATUSES_LABELS[row.getValue<RegistrationTrancheStatus>()],
    },
    { id: "restPayment", header: "Reszta płatności", accessorKey: "restPayment" },
    {
      id: "restPaymentStatus",
      header: "Reszta płatności status",
      accessorKey: "restPaymentStatus",
      cell: (row) => REGISTRATION_TRANCHE_PAYMENT_STATUSES_LABELS[row.getValue<RegistrationTrancheStatus>()],
    },
  ];

  return (
    <CourseCreateProvider>
      <PageMain>
        <HeaderWithBreadcrumbs
          breadcrumbs={[
            { title: "Kursy", path: routes.COURSES },
            { title: "Grupy kursów", path: routes.COURSE_GROUPS },
            { title: course.group.name, path: `${routes.COURSE_GROUPS_EDIT_COURSES(course.group.id.toString())}` },
            {
              title: [course.data.edition, course.data.variant].filter(Boolean).join(" · "),
              path: routes.COURSES_MANAGE(courseId),
            },
            { title: "Zaproś użytkowników" },
          ]}
          headerText="Zaproś użytkowników"
        />

        <form onSubmit={handleSubmit(onSubmit)}>
          <PaziaBox>
            Załaduj listę użytkowników do zaproszenia na kurs. Możesz to zrobić poprzez plik CSV lub ręcznie. Otrzymają
            oni wiadomość email z informacją o dopisaniu do kursu. Osoby nieposiadające konta w systemie zostaną
            zaproszone do jego utworzenia.
          </PaziaBox>

          <FormProvider {...methods}>
            <PaziaBox header="Ustal mapowanie płatności" className="mt-2 md:mt-6">
              <div className="flex gap-2">
                <PaziaSelect
                  required
                  label="Zaliczka"
                  className="w-1/2"
                  name="advanceCourseTrancheId"
                  options={tranches}
                  isLoading={loadingTranches}
                />
                <PaziaSelect
                  required
                  label="Pozostała kwota"
                  name="restCourseTrancheId"
                  className="w-1/2"
                  options={tranches}
                  isLoading={loadingTranches}
                />
              </div>
            </PaziaBox>

            <PaziaBox header="Lista użytkowników" className="mt-2 md:mt-6">
              <div className="ml-auto flex justify-end gap-2">
                <PaziaButton type="secondary" onClick={() => setModalOpen(true)} btnType="button">
                  Załaduj z pliku
                </PaziaButton>
                <PaziaButton type="secondary" btnType="button">
                  Dodaj
                </PaziaButton>
              </div>

              <TanTableClient data={users} columns={columns} hideBorder />
              <div className="mt-4 flex justify-end">
                <PaziaFormButton disabled={users.length < 1} />
              </div>
            </PaziaBox>
          </FormProvider>
        </form>

        <InviteUsersFromCsvModal
          isOpen={isModalOpen}
          onCloseRequest={() => setModalOpen(false)}
          onSubmit={handleImportUsers}
        />
      </PageMain>
    </CourseCreateProvider>
  );
};

export interface InviteUserData {
  email: string;
  firstName: string;
  surname: string;
  bornPlace?: string;
  address?: string;
  bornDateTime?: Date;
  pesel?: string;
  buyer?: string;
  phone?: string;
  buyerAddress?: string;
  buyerNIP?: string;
  advancePayment?: number;
  advancePaymentStatus?: number;
  restPayment?: number;
  restPaymentStatus?: number;
}

const schema = yup
  .object({
    advanceCourseTrancheId: yup.number().required(),
    restCourseTrancheId: yup.number().required(),
  })
  .required();

type FormValues = yup.InferType<typeof schema>;
